import React, { useEffect, useRef, useState } from 'react';
import {
  Badge, Button, Form, InputGroup, ListGroup, Modal, Spinner,
} from 'react-bootstrap';
import { DiscoverShelfBook, searchBooks } from './discoverShelvesApi';

// How long typing must pause before a search runs. Each keystroke restarts the wait.
const BOOK_SEARCH_DELAY_MS = 1000;
// Counted on the raw input: spaces and special characters count.
const BOOK_SEARCH_MIN_LENGTH = 3;

interface BookPickerProps {
  onSelect: (book: DiscoverShelfBook) => void;
  onCancel: () => void;
}

// Paste a UUID or search titles. Results arrive one server page at a time, so the browser never
// holds the full match set.
export function BookPicker(props: BookPickerProps) {
  const { onSelect, onCancel } = props;
  const [query, setQuery] = useState('');
  const [books, setBooks] = useState<DiscoverShelfBook[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  // True while a search is queued behind the typing delay, so the spinner shows straight away.
  const [waiting, setWaiting] = useState(false);
  const [searched, setSearched] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const runSearch = (q: string, nextPage: number) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    searchBooks(q, nextPage, controller.signal)
      .then((result) => {
        setBooks((current) => (nextPage === 0 ? result.books : [...current, ...result.books]));
        setHasMore(result.hasMore);
        setPage(nextPage);
        setSearched(true);
        setLoading(false);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    // Typing abandons any search in flight and restarts the delay.
    abortRef.current?.abort();
    setLoading(false);
    setBooks([]);
    setHasMore(false);
    setSearched(false);
    if (query.length < BOOK_SEARCH_MIN_LENGTH) {
      setWaiting(false);
      return undefined;
    }
    setWaiting(true);
    const timer = setTimeout(() => {
      setWaiting(false);
      runSearch(query, 0);
    }, BOOK_SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => () => abortRef.current?.abort(), []);

  return (
    <Modal show onHide={onCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add book</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InputGroup>
          <Form.Control
            autoFocus
            placeholder="Paste a book UUID or search by title"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {(waiting || loading) && (
            <InputGroup.Append>
              <InputGroup.Text>
                <Spinner animation="border" size="sm" role="status" aria-label="Searching" />
              </InputGroup.Text>
            </InputGroup.Append>
          )}
        </InputGroup>
        {query.length > 0 && query.length < BOOK_SEARCH_MIN_LENGTH && (
          <div className="small text-muted mt-1">Type at least {BOOK_SEARCH_MIN_LENGTH} characters</div>
        )}
        {searched && !books.length && !loading && !waiting && <div className="mt-2">Book not found</div>}
        <ListGroup className="mt-2" style={{ maxHeight: 420, overflowY: 'auto' }}>
          {books.map((book) => (
            <ListGroup.Item key={book.uuid} action onClick={() => onSelect(book)} className="d-flex align-items-center">
              <div
                style={{
                  width: 37,
                  height: 50,
                  flexShrink: 0,
                  borderRadius: 2,
                  background: book.coverUrl ? `center / cover no-repeat url("${book.coverUrl}")` : '#ddd',
                }}
              />
              <div className="ml-2" style={{ minWidth: 0 }}>
                <div className="font-weight-bold text-truncate">{book.title}</div>
                <div className="small text-muted">{book.uuid}</div>
              </div>
              {!book.published && <Badge variant="warning" className="ml-auto">Unpublished</Badge>}
            </ListGroup.Item>
          ))}
        </ListGroup>
        {loading && <div className="text-center mt-2"><Spinner animation="border" size="sm" /></div>}
        {hasMore && !loading && (
          <Button variant="link" onClick={() => runSearch(query, page + 1)}>Load more</Button>
        )}
      </Modal.Body>
    </Modal>
  );
}
